// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage, { LazyImageProps } from "@steiner/MobiusOverrides/LazyImage";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import React, { FC, useState } from "react";

interface CategoryCardProps {
    category?: Category;
    extendedStyles?: CategoryCardStyles;
}

type Props = CategoryCardProps;

export interface CategoryCardStyles {
    container?: InjectableCss;
    imageClickable?: ClickablePresentationProps;
    image?: LazyImageProps;
    link?: LinkPresentationProps;
}

export const categoryCardStyles: CategoryCardStyles = {};

const CategoryCard: FC<Props> = ({ category, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(categoryCardStyles, extendedStyles));

    if (!category) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="category-card">
            <Clickable {...styles.imageClickable} href={category.path}>
                <LazyImage
                    src={category.smallImagePath}
                    altText={`${translate("Image of ")}: ${category.shortDescription}`}
                    {...styles.image}
                />
            </Clickable>
            <Link href={category.path} {...styles.link} aria-label={translate("Category Name")}>
                {category.shortDescription}
            </Link>
        </StyledWrapper>
    );
};

export default CategoryCard;
